import React, { useEffect, useMemo, useRef, useState } from "react";
import { C, Crest, FONT, formatMatchdayDate } from "./common";

function yStep(max) {
  if (max <= 6) return 1;
  if (max <= 15) return 3;
  if (max <= 30) return 5;
  return 10;
}

/** Spreads label positions so crests at the same height don't stack; keeps them inside [top, bottom]. */
export function spreadLabels(items, gap, top, bottom) {
  const sorted = [...items].sort((a, b) => a.y - b.y || a.order - b.order);
  const room = bottom - top;
  const g = sorted.length > 1 ? Math.min(gap, room / (sorted.length - 1)) : gap;
  sorted.forEach((it, i) => {
    it.ly = Math.max(it.y, top);
    if (i > 0) it.ly = Math.max(it.ly, sorted[i - 1].ly + g);
  });
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const limit = i === sorted.length - 1 ? bottom : sorted[i + 1].ly - g;
    sorted[i].ly = Math.min(sorted[i].ly, limit);
  }
  return sorted;
}

/**
 * Cumulative points per team. The x-axis always runs Start → last matchday of the
 * season; lines stop at the selected matchday, where each team's crest sits.
 */
export default function PointsChart({ report, forecast = null, width: fixedWidth, height = 320, poster = false, lastMatchday = null }) {
  const wrapRef = useRef(null);
  const [measured, setMeasured] = useState(fixedWidth || 320);
  const [hover, setHover] = useState(null);
  const width = fixedWidth || measured;

  useEffect(() => {
    if (fixedWidth || !wrapRef.current) return undefined;
    const el = wrapRef.current;
    const update = () => setMeasured(Math.max(260, el.clientWidth));
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fixedWidth]);

  const wide = poster || width >= 520;
  const crest = poster ? 40 : width < 480 ? 22 : 28;
  const pad = { left: poster ? 48 : 30, right: crest / 2 + (poster ? 18 : 10), top: crest / 2 + 6, bottom: poster ? 50 : wide ? 40 : 28 };
  const played = report.matchday;
  const total = Math.max(played, lastMatchday ?? report.matchdays.length, 1);
  const dateOf = (i) => formatMatchdayDate(report.matchdays[i - 1]?.date, { month: "short", day: "numeric" });
  const leaderSet = useMemo(() => new Set(report.leaderIds), [report.leaderIds]);

  const fIdx = forecast && forecast.matchday > played && forecast.matchday <= total ? forecast.matchday : null;
  const endIdx = fIdx ?? played;
  const series = report.series.map((s, order) => {
    const values = [0, ...s.points];
    const last = values[values.length - 1];
    return { ...s, order, values, forecastValue: fIdx ? forecast.points[s.teamId] ?? last : null };
  });
  const endValue = (s) => (fIdx ? s.forecastValue : s.values[s.values.length - 1]);
  const maxPts = Math.max(3, ...series.flatMap((s) => [...s.values, s.forecastValue ?? 0]));
  const step = yStep(maxPts);
  const yMax = Math.ceil(maxPts / step) * step;
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const x = (i) => pad.left + (i / total) * plotW;
  const y = (v) => pad.top + plotH - (v / yMax) * plotH;
  const ticks = [];
  for (let v = 0; v <= yMax; v += step) ticks.push(v);

  const lastX = x(played);
  const endX = x(endIdx);
  const crestX = endX + crest / 2 + (poster ? 14 : 8);
  const labels = spreadLabels(
    series.map((s) => ({ teamId: s.teamId, order: leaderSet.has(s.teamId) ? -1 : s.order, y: y(endValue(s)) })),
    crest + 3,
    pad.top,
    pad.top + plotH
  );
  const labelById = Object.fromEntries(labels.map((l) => [l.teamId, l.ly]));

  const drawOrder = [...series].sort((a, b) => Number(leaderSet.has(a.teamId)) - Number(leaderSet.has(b.teamId)));
  const focusId = hover?.teamId ?? null;

  const onMove = (e) => {
    if (poster) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const i = Math.round(((e.clientX - rect.left - pad.left) / plotW) * total);
    if (i < 0 || i > endIdx) return setHover(null);
    setHover((h) => (h && h.index === i && h.teamId == null ? h : { index: i }));
  };

  const tooltipRows =
    hover && hover.index != null
      ? series
          .map((s) => ({ ...s, value: hover.index === fIdx ? s.forecastValue : s.values[hover.index] }))
          .filter((s) => focusId == null || s.teamId === focusId)
          .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name))
      : [];

  const font = poster ? 20 : 11;

  return (
    <div ref={wrapRef} style={{ width: fixedWidth || "100%", position: "relative", fontFamily: FONT }}>
      <div
        style={{ position: "relative", width, height, touchAction: "pan-y" }}
        onPointerMove={onMove}
        onPointerDown={onMove}
        onPointerLeave={() => setHover(null)}
      >
        <svg width={width} height={height} style={{ display: "block", overflow: "visible" }} role="img" aria-label="Cumulative points by matchday">
          {played < total && (
            <rect x={endX} y={pad.top} width={x(total) - endX} height={plotH} fill={C.faint} />
          )}
          {fIdx && (
            <g>
              <defs>
                <pattern id={`fc-${poster ? "p" : "w"}`} width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <line x1="0" y1="0" x2="0" y2="8" stroke={C.forecast} strokeWidth="2" opacity="0.18" />
                </pattern>
              </defs>
              <rect x={lastX} y={pad.top} width={endX - lastX} height={plotH} fill={`url(#fc-${poster ? "p" : "w"})`} />
              <text x={(lastX + endX) / 2} y={pad.top + (poster ? 22 : 13)} textAnchor="middle" fill={C.forecast} fontSize={poster ? 17 : 10} fontWeight={800} letterSpacing={1}>
                FORECAST
              </text>
            </g>
          )}
          {ticks.map((v) => (
            <g key={v}>
              <line x1={pad.left} x2={x(total)} y1={y(v)} y2={y(v)} stroke={C.grid} strokeDasharray={v === 0 ? undefined : "3 5"} />
              <text x={pad.left - 10} y={y(v)} dy="0.35em" textAnchor="end" fill={C.muted} fontSize={font} fontWeight={600}>
                {v}
              </text>
            </g>
          ))}
          {Array.from({ length: total + 1 }, (_, i) => {
            const fill = i === fIdx ? C.forecast : i === played ? C.accent : i > played ? "rgba(17,17,17,0.3)" : C.muted;
            const weight = i === played ? 800 : 600;
            const base = height - pad.bottom + (poster ? 24 : 16);
            if (i === 0)
              return (
                <text key={i} x={x(i)} y={base} textAnchor="middle" fill={C.muted} fontSize={font} fontWeight={600}>
                  Start
                </text>
              );
            return (
              <text key={i} x={x(i)} y={base} textAnchor="middle" fill={fill} fontWeight={weight}>
                {wide ? (
                  <>
                    <tspan x={x(i)} fontSize={font}>
                      MD{i}
                    </tspan>
                    <tspan x={x(i)} dy={poster ? 20 : 13} fontSize={poster ? 15 : 10} fontWeight={500}>
                      {i === fIdx ? "forecast" : dateOf(i)}
                    </tspan>
                  </>
                ) : (
                  <tspan fontSize={10}>{dateOf(i)}</tspan>
                )}
              </text>
            );
          })}
          {hover && hover.index != null && (
            <line x1={x(hover.index)} x2={x(hover.index)} y1={pad.top} y2={pad.top + plotH} stroke="rgba(17,17,17,0.2)" />
          )}
          {drawOrder.map((s) => {
            const lead = leaderSet.has(s.teamId);
            const focus = focusId === s.teamId;
            const opacity = focusId != null ? (focus ? 1 : 0.1) : lead ? 1 : 0.32;
            const sw = (lead || focus ? 3.5 : 2) * (poster ? 1.6 : 1);
            const pts = s.values.map((v, i) => `${x(i)},${y(v)}`).join(" ");
            const lastY = y(s.values[s.values.length - 1]);
            const endY = y(endValue(s));
            const dot = (lead ? 3.5 : 2.5) * (poster ? 1.5 : 1);
            return (
              <g key={s.teamId} opacity={opacity}>
                <polyline points={pts} fill="none" stroke={C.accent} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round" />
                {s.values.map((v, i) => (
                  <circle key={i} cx={x(i)} cy={y(v)} r={dot} fill={C.accent} />
                ))}
                {fIdx && (
                  <>
                    <line x1={lastX} y1={lastY} x2={endX} y2={endY} stroke={C.forecast} strokeWidth={sw} strokeDasharray={poster ? "10 8" : "5 4"} strokeLinecap="round" />
                    <circle cx={endX} cy={endY} r={dot + 1} fill={C.panel} stroke={C.forecast} strokeWidth={poster ? 2.5 : 1.6} />
                  </>
                )}
                {Math.abs(labelById[s.teamId] - endY) > 1 && (
                  <path d={`M${endX},${endY} L${crestX - crest / 2},${labelById[s.teamId]}`} stroke={fIdx ? C.forecast : C.accent} strokeWidth={poster ? 1.5 : 1} fill="none" />
                )}
              </g>
            );
          })}
        </svg>

        {series.map((s) => {
          const lead = leaderSet.has(s.teamId);
          return (
            <div
              key={s.teamId}
              onPointerEnter={() => !poster && setHover({ index: endIdx, teamId: s.teamId })}
              style={{
                position: "absolute",
                left: crestX - crest / 2,
                top: labelById[s.teamId] - crest / 2,
                opacity: focusId != null && focusId !== s.teamId ? 0.3 : 1,
                zIndex: lead ? 2 : 1,
              }}
            >
              <Crest url={s.logoUrl} name={s.name} size={crest} ring={lead ? C.accent : null} />
            </div>
          );
        })}

        {!poster && tooltipRows.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: 4,
              left: x(hover.index) > width / 2 ? Math.max(4, x(hover.index) - 204) : x(hover.index) + 12,
              width: 192,
              background: "rgba(255,255,255,0.98)",
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              padding: "8px 10px",
              pointerEvents: "none",
              zIndex: 5,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            }}
          >
            <div style={{ color: C.accent, fontSize: 11, fontWeight: 800, letterSpacing: 1, marginBottom: 4 }}>
              {hover.index === 0 ? "START" : hover.index === fIdx ? `FORECAST · AFTER MATCHDAY ${hover.index}` : `AFTER MATCHDAY ${hover.index}`}
            </div>
            {tooltipRows.map((r) => (
              <div key={r.teamId} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.text, padding: "2px 0" }}>
                <Crest url={r.logoUrl} name={r.name} size={16} />
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
                <b>{r.value}</b>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
